import React from 'react'

import LoadingWrapper from '../../widgets/loading'
import SendStatusCell from './send_status_cell'
import DailyStrip from './daily_strip'
import { COLUMN_TOOLTIPS, EMPTY_MESSAGE } from '../helpers/constants'
import { fmtCount } from '../helpers/helper'

// Loading / empty / error all render as one full-width row so the table never collapses.
const COLUMN_COUNT = 7

// Mailbox identity. A non-null `error` means that row's IMAP read failed; showing it right
// under the address is what tells the operator which mailbox to go and fix.
const MailboxCell = ({ item }) => (
  <div>
    <div className='am-mailbox-email'>{item.email || item.account}</div>
    {item.name ? <div className='am-mailbox-sub'>{item.name}</div> : null}
    {item.error ? <div className='am-mailbox-error'>{item.error}</div> : null}
  </div>
)

const RowActions = ({
  item,
  isExpanded,
  isConfirming,
  isSending,
  result,
  onToggleDaily,
  onTestSend,
  onDisarmTestSend
}) => {
  // A mailbox we could not read has nothing to chart, and a test send from it is pointless.
  const disabled = !!item.error

  let sendLabel = 'Test send'
  if (isSending) {
    sendLabel = 'Sending…'
  } else if (isConfirming) {
    sendLabel = 'Send for real?'
  }
  return (
    <div className='am-row-actions'>
      <span
        className={`am-status-toggle ${
          item.pool?.status === 'active' ? 'is-on' : 'is-off'
        }`}
        title={item.pool?.status || 'Unknown'}
      >
        <span className="am-status-toggle-knob" />
        <span>{item.pool?.status || 'Unknown'}</span>
      </span>
      <button
        type='button'
        className='am-btn'
        disabled={disabled}
        aria-expanded={isExpanded}
        onClick={() => onToggleDaily(item.account)}
      >
        {isExpanded ? 'Daily ▴' : 'Daily ▾'}
      </button>
      {/* Two-step arm (never one click): the first click only relabels this button, the
          second sends. One click is too easy to hit by accident on a table row, and every
          accidental press is a real email from a customer-facing domain. */}
      <button
        type='button'
        className={'am-btn' + (isConfirming ? ' am-btn-armed' : '')}
        disabled={disabled || isSending}
        title={`Sends a real email from ${item.email || item.account}`}
        onBlur={onDisarmTestSend}
        onClick={() => onTestSend(item.account)}
      >
        {sendLabel}
      </button>
      {result ? (
        <div className={'am-send-result' + (result.ok ? ' am-ok' : ' am-fail')}>
          {result.message}
        </div>
      ) : null}
    </div>
  )
}

/* The account table. Rows arrive from the API already sorted by lifetime `sent`
 * descending; that order is preserved and there is no client-side re-sort.
 */
const AccountTable = ({
  items,
  loading,
  error,
  expanded,
  daily,
  confirming,
  sending,
  sendResult,
  onToggleDaily,
  onTestSend,
  onDisarmTestSend
}) => {
  const renderFullWidth = (content, className) => (
    <tr>
      <td
        className={'am-fullwidth ' + className}
        colSpan={COLUMN_COUNT}
      >
        <div className='am-fullwidth-inner'>{content}</div>
      </td>
    </tr>
  )

  const renderBody = () => {
    if (loading) {
      return renderFullWidth(
        <LoadingWrapper
          type='spin'
          color='#9aa0a6'
          height={40}
          width={40}
        />,
        'am-loading'
      )
    }
    if (error) {
      return renderFullWidth(error, 'am-error')
    }
    if (!items.length) {
      return renderFullWidth(EMPTY_MESSAGE, 'am-empty')
    }

    return items.map((item) => {
      const isExpanded = expanded === item.account
      return (
        <React.Fragment key={item.account}>
          <tr>
            <td>
              <MailboxCell item={item} />
            </td>
            <td className='am-right'>{fmtCount(item.sentToday)}</td>
            <td className='am-right'>{fmtCount(item.sentYesterday)}</td>
            <td className='am-right'>{fmtCount(item.sent)}</td>
            <td className='am-right'>
              {/* pool.remaining.daily is null (not 0) when Redis could not be read — fmtCount
                  renders that as an em dash so "unknown" is never mistaken for "exhausted". */}
              {fmtCount(item.pool && item.pool.remaining && item.pool.remaining.daily)}
              {' / '}
              {fmtCount(item.pool && item.pool.limits && item.pool.limits.daily)}
            </td>
            <td>
              {/* Rendered even for a degraded row: send{} comes from cocloud's own rate
                  limiter, independent of any one mailbox's IMAP failure, so it stays
                  meaningful. */}
              <SendStatusCell send={item.send} />
            </td>
            <td>
              <RowActions
                item={item}
                isExpanded={isExpanded}
                isConfirming={confirming === item.account}
                isSending={sending === item.account}
                result={sendResult[item.account]}
                onToggleDaily={onToggleDaily}
                onTestSend={onTestSend}
                onDisarmTestSend={onDisarmTestSend}
              />
            </td>
          </tr>
          {isExpanded ? (
            <tr className='am-daily-row'>
              <td colSpan={COLUMN_COUNT}>
                <DailyStrip state={daily[item.account]} />
              </td>
            </tr>
          ) : null}
        </React.Fragment>
      )
    })
  }

  return (
    <div className='am-table-wrap'>
      <div className='am-table-scroll'>
        <table className='am-table'>
          {/* Widths live here rather than on the cells: the table is table-layout:fixed so
              the five numeric columns keep even, legible widths instead of collapsing to
              their content and bunching together in the middle. */}
          <colgroup>
            <col className='am-col-mailbox' />
            <col className='am-col-num' />
            <col className='am-col-num' />
            <col className='am-col-num' />
            <col className='am-col-remaining' />
            <col className='am-col-send' />
            <col className='am-col-actions' />
          </colgroup>
          <thead>
            <tr>
              <th>Mailbox</th>
              <th
                className='am-right'
                title={COLUMN_TOOLTIPS.sentToday}
              >
                Last 24h
              </th>
              {/* Never label this "24-48h" — see COLUMN_TOOLTIPS.sentYesterday. */}
              <th
                className='am-right'
                title={COLUMN_TOOLTIPS.sentYesterday}
              >
                Prev. UTC day
              </th>
              <th
                className='am-right'
                title={COLUMN_TOOLTIPS.sent}
              >
                Sent
              </th>
              <th
                className='am-right'
                title={COLUMN_TOOLTIPS.remainingToday}
              >
                Remaining today
              </th>
              <th>Can send</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {renderBody()}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AccountTable
